import { useState, useEffect, useCallback } from 'react';
import axios from '../../axiosConfig';
import dayjs from 'dayjs';
import { useOwnerCalendar } from '../../context/OwnerCalendarContext';

export const useOwnerCalendarData = ({ ownerId, schoolId }) => {
    const {
        currentWeekStart,
        selectedTrainer,
        selectedDogId,
        setSchoolSettings,
        setIsBooking,
        setIsCanceling,
    } = useOwnerCalendar();

    const [trainers, setTrainers] = useState([]);
    const [ownerDogs, setOwnerDogs] = useState([]);
    const [services, setServices] = useState([]);
    const [workingHours, setWorkingHours] = useState([]);
    const [trainings, setTrainings] = useState([]);
    const [slotBookings, setSlotBookings] = useState([]);
    const [lockedSlots, setLockedSlots] = useState([]);
    const [trainingLocks, setTrainingLocks] = useState([]);
    const [confirmedTrainings, setConfirmedTrainings] = useState([]);
    const [groupTrainings, setGroupTrainings] = useState([]);

    const [loading, setLoading] = useState({
        trainers: false,
        dogs: false,
        services: false,
        workingHours: false,
        trainings: false,
        availability: false,
        schoolSettings: false,
    });

    const [errors, setErrors] = useState({
        trainers: null,
        dogs: null,
        services: null,
        workingHours: null,
        trainings: null,
        availability: null,
        schoolSettings: null,
    });

    const getAuthHeader = useCallback(() => ({
        Authorization: `Bearer ${localStorage.getItem('jwt')}`
    }), []);

    const fetchTrainers = useCallback(async () => {
        if (!schoolId) return;

        setLoading(prev => ({ ...prev, trainers: true }));
        setErrors(prev => ({ ...prev, trainers: null }));

        try {
            const response = await axios.get(`/api/v1/trainer/find-all/${schoolId}`, {
                headers: getAuthHeader(),
            });
            setTrainers(response.data || []);
        } catch (error) {
            setErrors(prev => ({ ...prev, trainers: error.message }));
            console.error('Failed to fetch trainers:', error);
        } finally {
            setLoading(prev => ({ ...prev, trainers: false }));
        }
    }, [schoolId, getAuthHeader]);

    const fetchSchoolSettings = useCallback(async () => {
        if (!schoolId) return;

        setLoading(prev => ({ ...prev, schoolSettings: true }));
        setErrors(prev => ({ ...prev, schoolSettings: null }));

        try {
            const response = await axios.get(`/api/v1/school/${schoolId}/settings`, {
                headers: getAuthHeader(),
            });
            setSchoolSettings(response.data);
        } catch (error) {
            setErrors(prev => ({ ...prev, schoolSettings: error.message }));
            console.error('Failed to fetch school settings:', error);
        } finally {
            setLoading(prev => ({ ...prev, schoolSettings: false }));
        }
    }, [schoolId, getAuthHeader, setSchoolSettings]);

    const fetchOwnerDogs = useCallback(async () => {
        if (!ownerId) return;

        setLoading(prev => ({ ...prev, dogs: true }));
        setErrors(prev => ({ ...prev, dogs: null }));

        try {
            const response = await axios.get(`/api/v1/dog/${ownerId}/list`, {
                headers: getAuthHeader(),
            });
            setOwnerDogs(response.data || []);
        } catch (error) {
            setErrors(prev => ({ ...prev, dogs: error.message }));
            console.error('Failed to fetch dogs:', error);
        } finally {
            setLoading(prev => ({ ...prev, dogs: false }));
        }
    }, [ownerId, getAuthHeader]);

    const fetchServices = useCallback(async () => {
        if (!schoolId) return;

        setLoading(prev => ({ ...prev, services: true }));
        setErrors(prev => ({ ...prev, services: null }));

        try {
            const response = await axios.get(`/api/v1/services/school/${schoolId}`, {
                headers: getAuthHeader(),
            });
            setServices(response.data || []);
        } catch (error) {
            setErrors(prev => ({ ...prev, services: error.message }));
            console.error('Failed to fetch services:', error);
        } finally {
            setLoading(prev => ({ ...prev, services: false }));
        }
    }, [schoolId, getAuthHeader]);

    const fetchWorkingHours = useCallback(async () => {
        if (!selectedTrainer?.id) return;

        setLoading(prev => ({ ...prev, workingHours: true }));
        setErrors(prev => ({ ...prev, workingHours: null }));

        try {
            const response = await axios.get(`/api/v1/trainer/get-working-hours/${selectedTrainer.id}`, {
                headers: getAuthHeader(),
            });
            setWorkingHours(response.data || []);
        } catch (error) {
            setErrors(prev => ({ ...prev, workingHours: error.message }));
            console.error('Failed to fetch working hours:', error);
        } finally {
            setLoading(prev => ({ ...prev, workingHours: false }));
        }
    }, [selectedTrainer?.id, getAuthHeader]);

    const fetchOwnerTrainings = useCallback(async () => {
        if (!selectedDogId || !selectedTrainer?.id) return;

        setLoading(prev => ({ ...prev, trainings: true }));
        setErrors(prev => ({ ...prev, trainings: null }));

        const startTime = currentWeekStart.format('YYYY-MM-DDTHH:mm:ss');
        const endTime = currentWeekStart.add(6, 'day').endOf('day').format('YYYY-MM-DDTHH:mm:ss');

        try {
            const response = await axios.get(
                `/api/v1/training/getAllTrainingsForDogForTrainer/${selectedDogId}/${selectedTrainer.id}`,
                {
                    headers: getAuthHeader(),
                    params: { startTime, endTime }
                }
            );
            setTrainings(response.data || []);
        } catch (error) {
            setErrors(prev => ({ ...prev, trainings: error.message }));
            console.error('Failed to fetch trainings:', error);
        } finally {
            setLoading(prev => ({ ...prev, trainings: false }));
        }
    }, [selectedDogId, selectedTrainer?.id, currentWeekStart, getAuthHeader]);

    const fetchAvailabilityData = useCallback(async () => {
        if (!selectedTrainer?.id) return;

        setLoading(prev => ({ ...prev, availability: true }));
        setErrors(prev => ({ ...prev, availability: null }));

        const startTime = currentWeekStart.format('YYYY-MM-DDTHH:mm:ss');
        const endTime = currentWeekStart.add(6, 'day').endOf('day').format('YYYY-MM-DDTHH:mm:ss');

        try {
            const [lockSlotsRes, bookingsRes, trainingLocksRes, confirmedRes] = await Promise.all([
                axios.get(`/api/v1/training/lockedTrainingSlotsForTrainer/${selectedTrainer.id}`, {
                    headers: getAuthHeader(),
                    params: { startTime, endTime }
                }),
                axios.get(`/api/v1/training/bookings-for-week/${selectedTrainer.id}`, {
                    headers: getAuthHeader(),
                    params: { startTime, endTime }
                }),
                axios.get(`/api/v1/training/lock/${selectedTrainer.id}`, {
                    headers: getAuthHeader(),
                    params: { startTime, endTime }
                }),
                axios.get(`/api/v1/training/${selectedTrainer.id}/${selectedDogId}/getConfirmedTrainingsForTrainer`, {
                    headers: getAuthHeader(),
                    params: { startTime, endTime }
                })
            ]);

            setLockedSlots(lockSlotsRes.data || []);
            setSlotBookings(bookingsRes.data || []);
            setTrainingLocks(trainingLocksRes.data || []);
            setConfirmedTrainings(confirmedRes.data || []);
        } catch (error) {
            setErrors(prev => ({ ...prev, availability: error.message }));
            console.error('Failed to fetch availability data:', error);
        } finally {
            setLoading(prev => ({ ...prev, availability: false }));
        }
    }, [selectedTrainer?.id, selectedDogId, currentWeekStart, getAuthHeader]);

    const fetchGroupTrainings = useCallback(async () => {
        if (!selectedDogId) return;

        try {
            const response = await axios.get(`/api/v1/group-training/dog/${selectedDogId}`, {
                headers: getAuthHeader(),
            });

            const allGroupTrainings = response.data || [];
            const weekStart = currentWeekStart.startOf('isoWeek');
            const weekEnd = currentWeekStart.endOf('isoWeek');

            const filteredGroupTrainings = allGroupTrainings.filter(training => {
                const trainingStart = dayjs(training.startTime);
                return trainingStart.isSameOrAfter(weekStart, 'day') &&
                    trainingStart.isSameOrBefore(weekEnd, 'day');
            });

            setGroupTrainings(filteredGroupTrainings);
        } catch (error) {
            console.error('Failed to fetch group trainings:', error);
        }
    }, [selectedDogId, currentWeekStart, getAuthHeader]);

    const bookSingleTraining = useCallback(async (slotTime, serviceId = null, durationMinutes = 60, additionalInfo = '') => {
        if (!selectedTrainer?.id || !selectedDogId) {
            return { success: false, error: 'Missing trainer or dog selection' };
        }

        setIsBooking(true);
        const endTime = dayjs(slotTime).add(durationMinutes, 'minute');

        try {
            const response = await axios.post('/api/v1/training/requestBookingTraining', {
                trainerId: selectedTrainer.id,
                dogId: selectedDogId,
                hallId: null,
                serviceId: serviceId,
                additionalInfo: additionalInfo,
                startTime: dayjs(slotTime).format('YYYY-MM-DDTHH:mm:ss'),
                endTime: endTime.format('YYYY-MM-DDTHH:mm:ss')
            }, {
                headers: getAuthHeader()
            });

            await refreshCalendar();

            return { success: true, data: response.data };
        } catch (error) {
            console.error('Failed to book training:', error);
            return { success: false, error: error.message };
        } finally {
            setIsBooking(false);
        }
    }, [selectedTrainer?.id, selectedDogId, getAuthHeader, setIsBooking]);

    const bookMultipleTrainings = useCallback(async (slots, serviceId = null, additionalInfo = '', durationMinutes = 60) => {
        if (!selectedTrainer?.id || !selectedDogId || !slots.length) {
            return { success: false, error: 'Missing required data' };
        }

        setIsBooking(true);

        const requests = slots.map(slotStr => {
            const start = dayjs(slotStr, 'YYYY-MM-DDTHH:mm:ss');
            return {
                trainerId: selectedTrainer.id,
                dogId: selectedDogId,
                hallId: null,
                serviceId: serviceId,
                startTime: start.format('YYYY-MM-DDTHH:mm:ss'),
                endTime: start.add(durationMinutes, 'minute').format('YYYY-MM-DDTHH:mm:ss'),
                additionalInfo: additionalInfo
            };
        });

        try {
            const response = await axios.post('/api/v1/training/requestMultipleBookingTraining', requests, {
                headers: getAuthHeader()
            });

            await refreshCalendar();

            return { success: true, data: response.data };
        } catch (error) {
            console.error('Failed to book multiple trainings:', error);
            return { success: false, error: error.message };
        } finally {
            setIsBooking(false);
        }
    }, [selectedTrainer?.id, selectedDogId, getAuthHeader, setIsBooking]);

    const cancelTraining = useCallback(async (trainingId) => {
        if (!trainingId) return { success: false, error: 'No training ID provided' };

        setIsCanceling(true);

        try {
            await axios.delete(`/api/v1/training/${trainingId}/owner-cancel`, {
                headers: getAuthHeader()
            });

            setTrainings(prev => prev.filter(t => t.id !== trainingId));

            await refreshCalendar();

            return { success: true };
        } catch (error) {
            console.error('Failed to cancel training:', error);
            return { success: false, error: error.message };
        } finally {
            setIsCanceling(false);
        }
    }, [getAuthHeader, setIsCanceling]);

    const refreshCalendar = useCallback(async () => {
        await Promise.all([
            fetchOwnerTrainings(),
            fetchAvailabilityData(),
            fetchGroupTrainings()
        ]);
    }, [fetchOwnerTrainings, fetchAvailabilityData, fetchGroupTrainings]);

    useEffect(() => {
        fetchTrainers();
        fetchOwnerDogs();
        fetchServices();
        fetchSchoolSettings();
    }, [fetchTrainers, fetchOwnerDogs, fetchServices, fetchSchoolSettings]);

    useEffect(() => {
        if (selectedTrainer?.id) {
            fetchWorkingHours();
            fetchAvailabilityData();
        }
    }, [selectedTrainer?.id, fetchWorkingHours, fetchAvailabilityData]);

    useEffect(() => {
        if (selectedDogId && selectedTrainer?.id) {
            fetchOwnerTrainings();
            fetchGroupTrainings();
        }
    }, [selectedDogId, selectedTrainer?.id, currentWeekStart, fetchOwnerTrainings, fetchGroupTrainings]);

    const isLoading = Object.values(loading).some(val => val);
    const hasErrors = Object.values(errors).some(val => val !== null);

    return {
        trainers,
        ownerDogs,
        services,
        workingHours,
        trainings,
        slotBookings,
        lockedSlots,
        trainingLocks,
        confirmedTrainings,
        groupTrainings,
        loading,
        errors,
        isLoading,
        hasErrors,
        bookSingleTraining,
        bookMultipleTrainings,
        cancelTraining,
        refreshCalendar,
        fetchTrainers,
        fetchOwnerDogs,
        fetchServices,
        fetchWorkingHours,
        fetchOwnerTrainings,
        fetchAvailabilityData,
        fetchGroupTrainings,
    };
};